
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/components/ui/use-toast';
import { ArticleFormData } from '@/types/article-types';

export const useArticleFormSubmission = (
  isEditMode: boolean,
  id: string | undefined,
  createArticle: (formData: ArticleFormData) => Promise<string | null>,
  updateArticle: (id: string, formData: ArticleFormData) => Promise<boolean>,
  loadArticles: () => Promise<void>,
  validateForm: (formData: ArticleFormData) => boolean,
  setSaving: (saving: boolean) => void,
  setError: (error: string | null) => void,
  formValues: {
    title: string;
    leadText: string;
    content: string;
    headerImageUrl: string;
    authorId: string;
    selectedTags: string[];
    status: string;
    publishDate: Date | undefined;
  }
) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const handleSubmit = useCallback(async () => {
    const formData: ArticleFormData = {
      title: formValues.title,
      lead_text: formValues.leadText,
      content: formValues.content,
      header_image_url: formValues.headerImageUrl,
      author_id: formValues.authorId,
      tags: formValues.selectedTags,
      status: formValues.status as any,
      publish_date: formValues.publishDate ? formValues.publishDate.toISOString() : undefined
    };
    
    if (!validateForm(formData)) {
      return;
    }
    
    try {
      setSaving(true);
      setError(null);
      
      let result;
      if (isEditMode && id) {
        result = await updateArticle(id, formData);
      } else {
        result = await createArticle(formData);
      }
      
      if (!result) {
        throw new Error('Failed to save article');
      }
      
      await loadArticles();
      
      toast({
        title: `Article ${isEditMode ? 'Updated' : 'Created'}`,
        description: `${formValues.title} has been successfully ${isEditMode ? 'updated' : 'created'}`
      });
      
      // Use improved navigation with clear state
      console.log("Article saved, navigating to admin with articles tab");
      navigate('/admin', { 
        state: { 
          activeTab: 'articles',
          fromSubmit: true,
          timestamp: Date.now() // Help ensure state updates
        }
      });
    } catch (error: any) {
      console.error('Error saving article:', error);
      setError(error.message || `Failed to ${isEditMode ? 'update' : 'create'} article`);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || `Failed to ${isEditMode ? 'update' : 'create'} article`
      });
    } finally {
      setSaving(false);
    }
  }, [
    formValues, 
    validateForm, 
    setSaving, 
    setError, 
    isEditMode, 
    id, 
    updateArticle, 
    createArticle, 
    loadArticles, 
    toast, 
    navigate
  ]);
  
  const handleCancel = useCallback(() => {
    // Use improved navigation with preserved state
    console.log("Cancelling article form, navigating to admin with articles tab");
    navigate('/admin', { 
      state: { 
        activeTab: 'articles',
        fromCancel: true,
        timestamp: Date.now() // Help ensure state updates
      }
    });
  }, [navigate]);

  return {
    handleSubmit,
    handleCancel
  };
};
